import { native, type Product } from '@forest/native-services'
import { useStore } from '@nanostores/react'
import { motion } from 'motion/react'
import { useState, useTransition } from 'react'
import { m } from '~/paraglide/messages'
import { language, platform, shouldShowRestoreButton } from '~/params'
import { $products } from '~/store/products'
import { PlanOption } from './plan-option'

export function TrialPage(props: {
  startTrial: (product: Product) => Promise<unknown>
  onRestore: () => void
}) {
  const [selectedPlan, setSelectedPlan] = useState<'annual' | 'monthly'>(
    'annual',
  )
  const getSubscriptionInfoURL = () => {
    switch (import.meta.env.MODE) {
      case 'staging':
        return `https://forest-permanent-paywall-staging.seekrtech.com/${language}/details/`
      case 'production':
        return `https://forest-permanent-paywall.seekrtech.com/${language}/details/`
      default:
        return `https://forest-permanent-paywall.seekrtech.com/${language}/details/`
    }
  }
  const products = useStore($products)
  const isMonthlyNoTrial = selectedPlan === 'monthly'

  const [isPending, startTransition] = useTransition()
  const handleTrialButtonClick = () => {
    startTransition(async () => {
      native.general.logEvent({
        event_name: 'component_event',
        event_params: {
          type: 'btn',
          app_action: 'click',
          component_name: 'trial_paywall_trial',
        },
      })
      if (!products) return
      const monthlyNoTrial = products.monthlyNoTrial
      if (selectedPlan === 'monthly' && !monthlyNoTrial) return
      const product: Product =
        selectedPlan === 'monthly' && monthlyNoTrial
          ? monthlyNoTrial
          : products.annualEarlyBird

      await props.startTrial(product).catch(() => {
        // error is handled in mutation
      })
    })
  }

  const getButtonText = () => {
    if (isMonthlyNoTrial) return m.btn_unlock_plus()
    const eligibleTrialDays =
      selectedPlan === 'annual'
        ? products?.annualEarlyBird.eligible_trial_days
        : products?.monthlyEarlyBird.eligible_trial_days
    return m.btn_start_free_trial_day({
      free_trial_day: eligibleTrialDays ?? 0,
    })
  }

  if (!products) return null

  const calcDiscountPercentage = (
    originalProduct: Product,
    earlyBirdProduct: Product,
  ) => {
    return Math.round(
      ((originalProduct.price_value - earlyBirdProduct.price_value) /
        originalProduct.price_value) *
        100,
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative mx-auto h-full min-h-full max-w-[640px] space-y-12 overflow-y-auto px-8 pb-72 pt-[120px]"
    >
      <h1
        className="whitespace-pre-line px-4 text-center text-2xl font-bold text-white"
        dangerouslySetInnerHTML={{
          __html: m
            .subscription_walkthrough_p4_title({
              early_bird_discount_percent: calcDiscountPercentage(
                ...(selectedPlan === 'annual'
                  ? ([
                      products.annualOriginal,
                      products.annualEarlyBird,
                    ] as const)
                  : ([
                      products.monthlyOriginal,
                      products.monthlyEarlyBird,
                    ] as const)),
              ),
            })
            .replace('[%2$s]', 'color: #2AFD00;'),
        }}
      />

      <div className="space-y-3">
        <PlanOption
          plan="annual"
          title={
            platform === 'china_android' ? m.yearly_plan_cn() : m.yearly_plan()
          }
          earlyBirdProduct={products.annualEarlyBird}
          originalProduct={products.annualOriginal}
          selected={selectedPlan === 'annual'}
          selectPlan={setSelectedPlan}
        />
        <PlanOption
          plan="monthly"
          title={
            platform === 'china_android'
              ? m.monthly_plan_cn()
              : m.monthly_plan()
          }
          earlyBirdProduct={products.monthlyEarlyBird}
          originalProduct={products.monthlyOriginal}
          selected={selectedPlan === 'monthly'}
          selectPlan={setSelectedPlan}
          hideTag
        />

        <a
          className="block h-10 w-full text-center text-base font-bold text-white/60"
          href={`${getSubscriptionInfoURL()}?platform=${platform}`}
        >
          {m.subscription_view_complete_benefit()}
        </a>
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 bg-[#001721] px-6 pb-12 pt-3"
        style={{
          boxShadow: '0 -3px 3px 0 rgba(255, 255, 255, 0.20)',
        }}
      >
        <div className="mx-auto max-w-[640px] space-y-3">
          {!isMonthlyNoTrial && (
            <p className="text-center text-[#FFFFFFE6]">
              {m.subscription_walkthrough_p1_trial_fee()}
            </p>
          )}
          <button
            className="group h-[52px] w-full rounded-xl p-px [background-image:linear-gradient(180deg,#C5FF70_0%,#69FF78_45%,#30D3FF_100%)]"
            disabled={isPending}
            onClick={handleTrialButtonClick}
          >
            <span className="flex h-full w-full items-center justify-center rounded-[11px] font-bold text-[#176361] [background-image:linear-gradient(106deg,#C5FF70_13%,#30D3FF_97%)] group-active:[background-image:linear-gradient(90deg,#94D66E_0%,#2BB8D3_100%)]">
              {getButtonText()}
            </span>
          </button>

          {shouldShowRestoreButton && (
            <button
              className="w-full text-center text-xs text-white/60 underline"
              onClick={() => {
                native.general.logEvent({
                  event_name: 'component_event',
                  event_params: {
                    type: 'btn',
                    app_action: 'click',
                    component_name: 'trial_paywall_restore',
                  },
                })
                props.onRestore()
              }}
            >
              {m.restore_purchase()}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
